"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { Building2, ArrowRight, Search } from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { FilterBar } from "@/components/ui/filter-bar";

const ITEMS_PER_PAGE = 12;

interface CaseStudy {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  coverImage?: string;
  client?: string;
  industry?: string;
  technologies?: string;
}

export function CaseStudyListClient({ studies }: { studies: CaseStudy[] }) {
  const [page, setPage] = useState(1);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Extract unique industries
  const allIndustries = useMemo(() => {
    const set = new Set<string>();
    for (const s of studies) {
      if (s.industry) set.add(s.industry.trim());
    }
    return Array.from(set).sort();
  }, [studies]);

  // Filter
  const filtered = useMemo(() => {
    let result = studies;
    if (selectedIndustry) {
      result = result.filter((s) => s.industry?.trim() === selectedIndustry);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.excerpt?.toLowerCase().includes(q) ||
          s.client?.toLowerCase().includes(q) ||
          s.industry?.toLowerCase().includes(q) ||
          s.technologies?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [studies, selectedIndustry, searchQuery]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleIndustryChange = (industry: string | null) => {
    setSelectedIndustry(industry);
    setPage(1);
  };

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    setPage(1);
  };

  return (
    <section className="py-14 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Search + Filters */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search case studies..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            />
          </div>
          <FilterBar tags={allIndustries} selectedTag={selectedIndustry} onTagChange={handleIndustryChange} label="Filter by industry" />
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          {filtered.length} case stud{filtered.length !== 1 ? "ies" : "y"}
          {selectedIndustry && <> in <strong className="text-foreground">{selectedIndustry}</strong></>}
          {searchQuery && <> matching <strong className="text-foreground">&ldquo;{searchQuery}&rdquo;</strong></>}
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginated.map((study) => (
            <Link key={study.id} href={`/resources/case-studies/${study.slug}`}>
              <article className="group h-full flex flex-col rounded-2xl border border-border hover:border-primary/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden bg-card">
                {study.coverImage ? (
                  <div className="aspect-video bg-muted overflow-hidden">
                    <img src={study.coverImage} alt={study.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-violet-600/10 to-blue-600/10 flex items-center justify-center">
                    <Building2 className="w-12 h-12 text-primary/30" />
                  </div>
                )}
                <div className="flex-1 p-5">
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {study.industry && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/40 px-2.5 py-0.5 rounded-full">
                        <Building2 className="w-3 h-3" />
                        {study.industry}
                      </span>
                    )}
                    {study.client && (
                      <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-0.5 rounded-full">{study.client}</span>
                    )}
                  </div>
                  <h2 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">{study.title}</h2>
                  {study.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{study.excerpt}</p>}
                  {study.technologies && (
                    <div className="mt-4 pt-3 border-t border-border/50 flex flex-wrap gap-1.5">
                      {String(study.technologies).split(",").slice(0, 3).map((tech) => (
                        <span key={tech} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-md font-medium">{tech.trim()}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                    Read case study <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {paginated.length === 0 && (
          <div className="text-center py-16">
            <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No case studies found matching your criteria.</p>
          </div>
        )}

        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </section>
  );
}
